/**
 * useTaskContent - Deliverables, inputs, dates, and metadata changes
 * 
 * Composes smaller hooks for better maintainability:
 * - useTaskArtifacts: deliverables, inputs, attachments
 * - useTaskDates: due date, estimated duration, timestamps, date ranges
 * - useTaskMetadata: priority, difficulty
 */

import { useTaskArtifacts } from './content/useTaskArtifacts';
import { useTaskDates } from './content/useTaskDates';
import { useTaskMetadata } from './content/useTaskMetadata';
import { useTaskReminders } from './content/useTaskReminders';

export const useTaskContent = (localSprint, setLocalSprint, recordAction, onDateValidationError) => {
  const artifacts = useTaskArtifacts(localSprint, setLocalSprint, recordAction);
  const dates = useTaskDates(localSprint, setLocalSprint, recordAction, onDateValidationError);
  const metadata = useTaskMetadata(localSprint, setLocalSprint, recordAction);
  const reminders = useTaskReminders(localSprint, setLocalSprint, recordAction);

  return {
    // Notes: user-visible (task.notes) vs admin-only (task.internalNotes)
    handleNotesChange: artifacts.handleNotesChange,
    handleInternalNotesChange: artifacts.handleInternalNotesChange,
    // Block-editor mission docs (task.docUser / task.docAdmin)
    handleMissionDocChange: artifacts.handleMissionDocChange,
    // Per-mission tick / upload-field overrides for linked Docs
    handleMissionDocTicksChange: artifacts.handleMissionDocTicksChange,
    handleMissionDocFilesChange: artifacts.handleMissionDocFilesChange,
    handleMissionVariablesChange: artifacts.handleMissionVariablesChange,
    // Docs attached to a mission (task.attachedDocs) + plaintext quick notes
    handleAttachedDocsChange: artifacts.handleAttachedDocsChange,
    handleQuickNotesChange: artifacts.handleQuickNotesChange,
    handleMissionDocsMigrated: artifacts.handleMissionDocsMigrated,
    handleClearMissionLegacyNotes: artifacts.handleClearMissionLegacyNotes,
    // Artifacts (deliverables, inputs, attachments)
    handleDeliverablesChange: artifacts.handleDeliverablesChange,
    handleInputsChange: artifacts.handleInputsChange,
    handleAttachmentCountChange: artifacts.handleAttachmentCountChange,
    // Dates
    handleDueDateChange: dates.handleDueDateChange,
    handleEstimatedDurationMinutesChange: dates.handleEstimatedDurationMinutesChange,
    handleCreatedAtChange: dates.handleCreatedAtChange,
    handleStartedAtChange: dates.handleStartedAtChange,
    handleCompletedAtChange: dates.handleCompletedAtChange,
    handleUpdatedAtChange: dates.handleUpdatedAtChange,
    handleTaskStartDateChange: dates.handleTaskStartDateChange,
    handleTaskEndDateChange: dates.handleTaskEndDateChange,
    handleTaskDateRangeChange: dates.handleTaskDateRangeChange,
    handleRemindersChange: reminders.handleRemindersChange,
    // Metadata
    handlePriorityChange: metadata.handlePriorityChange,
    handleDifficultyChange: metadata.handleDifficultyChange,
    // Custom fields
    handleCustomFieldValueChange: metadata.handleCustomFieldValueChange,
  };
};

export default useTaskContent;
